import { toast } from "react-toastify";
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";

import { routes } from "../routing/routes";
import { LS_AUTH_TOKEN_KEY } from "../utils/common";
import { REFRESH_TOKEN_API_URL, REFRESH_TOKEN_ENDPOINT, refreshToken } from "../../features/auth/authApi";

let isRefreshing = false;
let failedQueue: { resolve: (value?: any) => void; reject: (error: any) => void; config: InternalAxiosRequestConfig }[] = [];

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem(LS_AUTH_TOKEN_KEY);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onResponseError = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config;

  if (!originalRequest) {
    return Promise.reject(error);
  }
  if (originalRequest.headers?.bypassErrorHandling) {
    delete originalRequest.headers.bypassErrorHandling;
    return Promise.reject(error);
  }

  const status = error.response?.status;

  switch (status) {
    case 401:
      if (originalRequest.url === REFRESH_TOKEN_API_URL || originalRequest.url?.endsWith(REFRESH_TOKEN_ENDPOINT)) {
        toast.error("Sesiunea a expirat, te rog sa te autentifici din nou!");
        window.location.href = routes.logout.path;
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        try {
          isRefreshing = true;
          const { token } = await refreshToken();
          processQueue(null, token);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return axios(originalRequest);
        } catch (err) {
          processQueue(err, null);
          toast.error("Sesiunea a expirat, te rog sa te autentifici din nou!");
          window.location.href = routes.logout.path;
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }
    case 403:
      toast.error("Nu ai suficiente drepturi pentru a accesa aceasta sectiune!");
      break;

    default:
      const response = error?.response?.data as any;
      const message = response?.detail || error?.message;
      toast.error(`A intervenit o eroare, te rog incearca mai tarziu! [${message}]`);
      break;
  }

  return Promise.reject(error);
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((queuedRequest) => {
    if (error) {
      queuedRequest.reject(error);
    } else {
      if (token && queuedRequest.config.headers) {
        queuedRequest.config.headers.Authorization = `Bearer ${token}`;
      }
      queuedRequest.resolve(axios(queuedRequest.config));
    }
  });
  failedQueue = [];
};

axios.interceptors.request.use(onRequest, onRequestError);
axios.interceptors.response.use(onResponse, onResponseError);
